import { Grid } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiCard from "components/base/UiCard";
import UiFieldGroup from "components/base/UiFieldGroup";
import UiTypography from "components/base/UiTypography";
import {
  AdditionalCostFeeType,
  ProposalAdditionalCost,
  ProposalProduct,
} from "generated/graphql";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { toCents } from "utils/money/toCents";
import React, { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { UpdateProposalProductInputSchema } from "DDD/action-objects/StageProductUpdate";
import { sentenceCase } from "change-case";
import CostSection, { CostRow } from "./CostSection";
import { ADDITIONAL_COST_COLUMNS } from "./costColumnDefinitions";
import ExpandedWrapper from "./ExpandedWrapper";
import { NUMERIC_SET_VALUE_AS } from "./formUtils";
import StatementOfWork from "./StatementOfWork";
import useMarkupInputProps from "./useMarkupInputProps";

type UnitPricedSectionProps = {
  product: ProposalProduct;
  onMeasureJob?: () => void;
  onAddAdditionalCost?: () => void;
  onDeleteAdditionalCost: (id: string) => void;
};

function overrideOrEmpty(
  override: number | null | undefined,
  formatter: (v: number) => string
): string {
  return override != null ? formatter(override) : "";
}

const toFloatOrZero = (v: string) => parseFloat(v) || 0;
const toCentsOrZero = (v: string) => (v === "" ? 0 : toCents(parseFloat(v) || 0));

export default function UnitPricedSection({
  product,
  onMeasureJob,
  onAddAdditionalCost,
  onDeleteAdditionalCost,
}: UnitPricedSectionProps) {
  const {
    register,
    setValue: setFormValue,
    getValues,
  } = useFormContext<UpdateProposalProductInputSchema>();
  const markupInputProps = useMarkupInputProps({
    register,
    setValue: setFormValue,
  });

  const [deletedACIds, setDeletedACIds] = useState<Set<string>>(() => new Set());

  const fields = [
    {
      label: "Unit Price",
      value: formatCentsToUSD(
        product.material.unitPriceOverride ?? product.material.unitPrice ?? 0
      ),
      name: "material.unitPrice",
    },
    {
      label: "Profit Markup %",
      value: String(
        product.material.markupPercentageOverride ?? product.material.markupPercentage ?? 0
      ),
      name: "material.markup",
      inputProps: markupInputProps,
    },
    {
      label: "Overhead %",
      value: String(
        product.material.overheadPercentageOverride ?? product.material.overheadPercentage ?? 0
      ),
      name: "material.overheadPercentage",
    },
    {
      label: "Indirect %",
      value: String(
        product.material.indirectPercentageOverride ?? product.material.indirectPercentage ?? 0
      ),
      name: "material.indirectPercentage",
    },
  ];

  const additionalCostRows: CostRow[] = (product.additionalCosts ?? []).map((ac) => {
    const feeType = ac.feeType as AdditionalCostFeeType | undefined;
    const feeDisabled =
      feeType === AdditionalCostFeeType.JOB_LENGTH || feeType === AdditionalCostFeeType.FLAT;
    const costDisabled = feeType === AdditionalCostFeeType.JOB_COST;
    const feeAmount = ac.feeAmountOverride ?? ac.feeAmount ?? 0;

    return {
      name: ac.name,
      costType: ac.feeType ? sentenceCase(ac.feeType) : "Fee",
      percentOrFee: String(feeAmount),
      quantity: String(ac.quantity ?? 1),
      rate: ((ac.cost ?? 0) / 100).toFixed(2),
      cost: formatCentsToUSD(+(ac as unknown as Record<string, number>).costTotal || 0),
      showOnProposal: ac.showOnProposal ?? false,
      __values: {
        percentOrFee: overrideOrEmpty(ac.feeAmountOverride, String),
        quantity: overrideOrEmpty(ac.quantityOverride, String),
        rate: overrideOrEmpty(ac.costOverride, (v) => (v / 100).toFixed(2)),
      },
      __columnOverrides: {
        ...(feeDisabled && { percentOrFee: { readOnly: true } }),
        ...(costDisabled && { rate: { readOnly: true } }),
      },
    };
  });

  useEffect(() => {
    setDeletedACIds(new Set());
  }, [product.id, (product.additionalCosts ?? []).length]);

  const visibleAdditionalCostRows: CostRow[] = (product.additionalCosts ?? []).flatMap(
    (item, sourceIndex) => {
      if (deletedACIds.has(item.id)) return [];
      const formIndex = (getValues("additionalCosts") ?? []).findIndex(
        (entry: any) => entry.id === item.id
      );
      if (formIndex < 0) return [];
      return [{ ...additionalCostRows[sourceIndex], __rowIndex: formIndex, __rowKey: item.id }];
    }
  );

  const handleAdditionalCostChange = useCallback(
    (rowIndex: number, colKey: string, value: string) => {
      if (colKey === "percentOrFee") {
        setFormValue(`additionalCosts.${rowIndex}.feeAmount` as any, toFloatOrZero(value), {
          shouldDirty: true,
        });
        return;
      }
      if (colKey === "quantity") {
        setFormValue(`additionalCosts.${rowIndex}.quantity` as any, toFloatOrZero(value), {
          shouldDirty: true,
        });
        return;
      }
      if (colKey === "rate") {
        setFormValue(`additionalCosts.${rowIndex}.cost` as any, toCentsOrZero(value), {
          shouldDirty: true,
        });
      }
    },
    [setFormValue]
  );

  const handleAdditionalCostCheckbox = useCallback(
    (rowIndex: number, colKey: string, checked: boolean) => {
      if (colKey === "showOnProposal") {
        setFormValue(`additionalCosts.${rowIndex}.showOnProposal` as any, checked, {
          shouldDirty: true,
        });
      }
    },
    [setFormValue]
  );

  const handleDeleteAdditionalCostWrapped = useCallback(
    (formIndex: number) => {
      const item = (getValues("additionalCosts") ?? [])[formIndex] as ProposalAdditionalCost | undefined;
      if (item) {
        setDeletedACIds((prev) => new Set(prev).add(item.id));
        onDeleteAdditionalCost(item.id);
      }
    },
    [getValues, onDeleteAdditionalCost]
  );

  return (
    <ExpandedWrapper>
      <UiCard variant="inner">
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb="8px">
          <UiTypography uiVariant="heading-sm">Profit and Production</UiTypography>
          {onMeasureJob && (
            <UiButton uiVariant="primary-grey" onClick={onMeasureJob}>
              Measure job
            </UiButton>
          )}
        </MDBox>
        <Grid container spacing="8px">
          {fields.map((field) => (
            <Grid item xs={6} md={3} key={field.label}>
              <UiFieldGroup
                label={field.label}
                value={field.value}
                textAlign="right"
                inputProps={
                  (field as { inputProps?: Record<string, unknown> }).inputProps ??
                  register(field.name as any, NUMERIC_SET_VALUE_AS)
                }
              />
            </Grid>
          ))}
        </Grid>
      </UiCard>

      <StatementOfWork />

      <CostSection
        title="Additional Costs"
        total={formatCentsToUSD(product.costAdditional)}
        columns={ADDITIONAL_COST_COLUMNS}
        rows={visibleAdditionalCostRows}
        onAdd={onAddAdditionalCost}
        onFieldChange={handleAdditionalCostChange}
        onCheckboxChange={handleAdditionalCostCheckbox}
        onDelete={handleDeleteAdditionalCostWrapped}
      />
    </ExpandedWrapper>
  );
}
